import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import TermsDocument from '../components/TermsDocument';
import { PARTNERS_SITE_URL, PRIVACY_POLICY_URL } from '../lib/links';

/*
 * The partner application — six questions.
 *
 * What this replaced asked twenty-one, across three steps, and ended by promising that a
 * human would come back within twelve working hours. Every question was defensible on its
 * own; together they were a qualification interview standing between an interested
 * operator and any contact with us, and the applicant paid for all of it before getting
 * anything back.
 *
 * The rule now: ASK ONLY WHAT EARNS THE CALL. Six answers do that. Everything else — the
 * agency name, the book's shape, the verticals, the tools we would be replacing — is
 * asked afterwards, from an emailed link, once a call is already in the diary. That is
 * `ContinuePage`, and it is where the scorecard finally runs.
 *
 * Two things follow that are worth not undoing:
 *
 *   1. NO PASSWORD HERE. It is chosen at the end of the second sitting, which is also
 *      what proves the address. Inventing a password is a strange thing to ask of
 *      somebody who has not spoken to you yet.
 *
 *   2. FREE-MAIL IS ACCEPTED. The partners we want include automation freelancers and
 *      two-person studios who run real client work from a gmail address. Where the domain
 *      cannot identify them we ask for a work profile instead, which is one field rather
 *      than a closed door.
 *
 * The options below match `PARTNER_TYPE_LABELS` and `RUNS_ON_LABELS` in backend/server.py.
 * Same rule the scorecard already lives by: the form and the vocabulary are one artefact
 * in two places, edited together or not at all.
 */

const FREE_MAIL = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.in', 'yahoo.in',
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'rediffmail.com', 'rediff.com', 'protonmail.com', 'proton.me', 'pm.me',
  'icloud.com', 'me.com', 'mac.com', 'aol.com', 'zoho.com', 'zohomail.com',
  'gmx.com', 'mail.com', 'yandex.com', 'ymail.com', 'inbox.com', 'hotmail.co.uk',
];

const isFreeMail = (email) => FREE_MAIL.includes((email || '').trim().toLowerCase().split('@')[1] || '');

const BUSINESS_TYPES = [
  { value: 'agency', label: 'An agency', help: 'You run marketing or sales delivery for a set of clients.' },
  { value: 'tech_partner', label: 'A technology partner', help: 'You build or integrate software for clients.' },
  { value: 'freelancer', label: 'An automation freelancer', help: 'You build automations and campaigns, mostly solo.' },
  { value: 'consultant', label: 'A consultant', help: 'You advise, and you are close enough to delivery to run it.' },
];

const CLIENT_COUNTS = [
  { value: '20+', label: '20 or more' },
  { value: '10-19', label: '10 to 19' },
  { value: '5-9', label: '5 to 9' },
  { value: '3-4', label: '3 or 4' },
  { value: '1-2', label: '1 or 2' },
  { value: '0', label: 'None yet' },
];

// What their client work runs on today. The most useful thing we collect: it is what we
// would be replacing, and it decides what the call should actually show them.
const RUNS_ON = [
  { value: 'crm', label: 'A CRM', hint: 'Zoho, HubSpot, Salesforce, anything else' },
  { value: 'workflow_builder', label: 'A workflow builder', hint: 'n8n, Make, Zapier, anything else' },
  { value: 'marketing_platform', label: 'A marketing platform', hint: 'MoEngage, CleverTap, WebEngage, Klaviyo' },
  { value: 'own_platform', label: 'Our own platform', hint: 'Something you built yourselves' },
  { value: 'sheets', label: 'Google Sheets or Excel', hint: '' },
  { value: 'nothing', label: 'Nothing yet', hint: '' },
  { value: 'other', label: 'Something else', hint: '' },
];

/** A single-answer question. Radix radios rather than styled buttons, so the whole group
 *  is one tab stop and arrow keys move between options. */
const ChoiceQuestion = ({ field, label, help, options, value, onChange }) => (
  <fieldset>
    <legend className="text-sm font-medium text-[#1A1A1A]">
      {label} <span className="text-red-500">*</span>
    </legend>
    {help && <p className="mt-1 text-xs leading-relaxed text-[#64748B]">{help}</p>}
    <RadioGroup value={value} onValueChange={onChange} className="mt-3 gap-2" data-testid={`question-${field}`}>
      {options.map((o) => {
        const id = `${field}-${o.value}`;
        const selected = value === o.value;
        return (
          <div
            key={o.value}
            className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
              selected ? 'border-[#ee785b] bg-[#ee785b]/[0.06]' : 'border-[#E2E2DF] bg-white hover:border-[#cbd5e1]'
            }`}
          >
            <RadioGroupItem value={o.value} id={id} className="mt-0.5 border-[#94A3B8] text-[#ee785b]" />
            <Label htmlFor={id} className="cursor-pointer text-sm font-normal leading-relaxed text-[#1A1A1A]">
              {o.label}
              {o.help && <span className="mt-0.5 block text-xs font-normal text-[#64748B]">{o.help}</span>}
            </Label>
          </div>
        );
      })}
    </RadioGroup>
  </fieldset>
);

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    mobile: '+91 ',
    business_type: '',
    client_count: '',
    runs_on: [],
    work_proof: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsVersion, setTermsVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const toggleRunsOn = (v) => {
    setFormData((prev) => ({
      ...prev,
      runs_on: prev.runs_on.includes(v) ? prev.runs_on.filter((x) => x !== v) : [...prev.runs_on, v],
    }));
  };

  const needsWorkProof = isFreeMail(formData.email);

  /** Validate before submitting. The work-proof rule is the server's (backend/server.py
   *  register) — checked here too so an applicant learns at the field. */
  const formError = () => {
    if (!formData.full_name.trim()) return 'Please tell us your name.';
    if (!formData.email.trim()) return 'Please enter your email address.';
    const digits = formData.mobile.replace(/[\s\-+]/g, '');
    if (!/^\d{10,12}$/.test(digits)) return 'Please enter a valid mobile number (10-12 digits).';
    if (!formData.business_type) return 'Please tell us which of these you are.';
    if (!formData.client_count) return 'Please tell us how many clients you run today.';
    if (!formData.runs_on.length) return 'Please tell us what you run client work on today.';
    if (needsWorkProof && !formData.work_proof.trim()) {
      return 'Please add a website or LinkedIn profile so we know who we would be partnering with.';
    }
    if (!agreedToTerms) return 'Please read and accept the partner terms.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = formError();
    if (error) {
      toast.error(error);
      return;
    }

    setLoading(true);
    try {
      // The server records the acceptance and rejects the application without this.
      // It takes the version and hash from its own constants, never from here.
      await register({ ...formData, accepted_terms: true });
      // Straight to the booking route. Registering signs them in, and PublicRoute bounces
      // an authenticated user off /register the moment it notices — so anything rendered
      // here instead of navigated to is never seen.
      navigate('/booked', { replace: true });
    } catch (error) {
      console.error('Application error:', error);
      // A colleague on the same domain is no longer refused — it is recorded and flagged
      // to whoever approves. So there is nothing left here but a plain failure.
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <a
          href={PARTNERS_SITE_URL}
          className="mb-8 inline-flex items-center gap-2 text-[#64748B] transition-colors hover:text-[#1A1A1A]"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">Back to Expedify Partners</span>
        </a>

        <div className="rounded-2xl border border-[#E2E2DF] bg-white p-8 shadow-sm">
          <a href={PARTNERS_SITE_URL} className="mb-6 inline-block">
            <img src="/light_logo.webp" alt="Expedify Logo" className="h-10 w-auto" />
          </a>

          <h1 className="mb-2 text-2xl font-bold text-[#1A1A1A]">Apply as a partner</h1>
          <p className="mb-8 text-[#64748B]">
            Six questions, then you book your call. There is no screening queue to wait in. The rest
            of the application comes later, by email, once the call is in your diary.
          </p>

          <form onSubmit={handleSubmit} className="space-y-7">
            <div>
              <Label htmlFor="full_name" className="text-sm font-medium text-[#1A1A1A]">
                Your name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                placeholder="Enter your full name"
                className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]"
                data-testid="register-name"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-[#1A1A1A]">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="you@youragency.com"
                  className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]"
                  data-testid="register-email"
                />
              </div>
              <div>
                <Label htmlFor="mobile" className="text-sm font-medium text-[#1A1A1A]">
                  WhatsApp number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) => handleChange('mobile', e.target.value)}
                  placeholder="+91 98765 43210"
                  className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]"
                  data-testid="register-mobile"
                />
              </div>
            </div>

            {/* Only when the domain cannot identify them. A work address answers this by
                itself, so most applicants never see this field at all. */}
            {needsWorkProof && (
              <div>
                <Label htmlFor="work_proof" className="text-sm font-medium text-[#1A1A1A]">
                  Website or LinkedIn profile <span className="text-red-500">*</span>
                </Label>
                <p className="mt-1 text-xs leading-relaxed text-[#64748B]">
                  A personal email domain does not tell us who we would be partnering with. One link
                  that does is enough, and it can be anything: your site, your LinkedIn, a portfolio.
                </p>
                <Input
                  id="work_proof"
                  value={formData.work_proof}
                  onChange={(e) => handleChange('work_proof', e.target.value)}
                  placeholder="linkedin.com/in/yourprofile"
                  className="mt-1.5 h-11 rounded-lg border-[#E2E2DF]"
                  data-testid="register-work-proof"
                />
              </div>
            )}

            <ChoiceQuestion
              field="business_type"
              label="Which of these are you?"
              options={BUSINESS_TYPES}
              value={formData.business_type}
              onChange={(v) => handleChange('business_type', v)}
            />

            <ChoiceQuestion
              field="client_count"
              label="How many clients do you run today?"
              help="There is no minimum. It tells us what the call should be about."
              options={CLIENT_COUNTS}
              value={formData.client_count}
              onChange={(v) => handleChange('client_count', v)}
            />

            <fieldset>
              <legend className="text-sm font-medium text-[#1A1A1A]">
                What do you run that client work on today? <span className="text-red-500">*</span>
              </legend>
              <p className="mt-1 text-xs leading-relaxed text-[#64748B]">
                Pick any that apply. This is the one that shapes your call, because it is what we
                would be replacing.
              </p>
              <div className="mt-3 space-y-2" data-testid="question-runs_on">
                {RUNS_ON.map((o) => {
                  const on = formData.runs_on.includes(o.value);
                  return (
                    <div
                      key={o.value}
                      className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                        on ? 'border-[#ee785b] bg-[#ee785b]/[0.06]' : 'border-[#E2E2DF] bg-white hover:border-[#cbd5e1]'
                      }`}
                    >
                      <Checkbox
                        id={`runs_on-${o.value}`}
                        checked={on}
                        onCheckedChange={() => toggleRunsOn(o.value)}
                        className="mt-0.5"
                      />
                      <Label
                        htmlFor={`runs_on-${o.value}`}
                        className="cursor-pointer text-sm font-normal leading-relaxed text-[#1A1A1A]"
                      >
                        {o.label}
                        {o.hint && <span className="mt-0.5 block text-xs text-[#64748B]">{o.hint}</span>}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </fieldset>

            {/* Terms — starts unticked, and the applicant must be able to read the document
                before agreeing to it. Ticking is what the acceptance record attests to. */}
            <div className="flex items-start gap-3 rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-4">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                className="mt-0.5"
                data-testid="register-terms"
              />
              <Label htmlFor="terms" className="cursor-pointer text-sm leading-relaxed text-[#64748B]">
                I have read and agree to the{' '}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setTermsOpen(true);
                  }}
                  className="text-[#ee785b] hover:underline"
                  data-testid="register-terms-link"
                >
                  Expedify partner terms{termsVersion ? ` (v${termsVersion})` : ''}
                </button>{' '}
                and the{' '}
                {/* A real link to a real page. What makes the agreement provable is that
                    §17.6 incorporates this policy, so the hash recorded at acceptance
                    covers the reference to it; the link is how someone reads it first. */}
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#ee785b] hover:underline"
                  data-testid="register-privacy-link"
                >
                  Privacy Policy
                </a>
                .
              </Label>
            </div>

            <Button
              type="submit"
              disabled={loading || !agreedToTerms}
              className="btn-press h-12 w-full rounded-lg bg-[#ef785b] font-semibold text-white hover:bg-[#D44017]"
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                  Submitting...
                </span>
              ) : (
                <>
                  Apply and book my call <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#64748B]">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-[#ef785b] hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>

        {/* What happens next, on the page where someone is deciding whether to start. */}
        <div className="mt-6 rounded-2xl border border-[#E2E2DF] bg-white p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">What happens next</p>
          <ol className="mt-4 space-y-3">
            {[
              'You book your call on the next screen. Nobody screens you first.',
              'We email you a link to finish the application whenever it suits you.',
              'What you tell us there decides the demo we build on your own vertical.',
              'Terms, rate card, and a workspace your team is certified to operate.',
            ].map((s, i) => (
              <li key={s} className="flex items-start gap-3 text-sm leading-relaxed text-[#64748B]">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#ee785b]/10 font-mono text-[10px] font-semibold text-[#ee785b]">
                  {i + 1}
                </span>
                {s}
              </li>
            ))}
          </ol>
        </div>
      </div>

      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Expedify partner terms</DialogTitle>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-[#E2E2DF] bg-white p-5">
            <TermsDocument onLoad={({ version }) => setTermsVersion(version)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermsOpen(false)} className="border-[#E2E2DF]">
              Close
            </Button>
            {/* Agreeing from inside the document is the same tick as the box outside it. */}
            <Button
              onClick={() => {
                setAgreedToTerms(true);
                setTermsOpen(false);
              }}
              className="bg-[#ee785b] text-white hover:bg-[#d66547]"
              data-testid="register-terms-accept"
            >
              I agree
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegisterPage;
